import React from "react";
import { Link } from "react-router-dom";
import { Cpu, Code2, BarChart3, Compass, Palette, Sparkles, Check, ArrowRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, organizationSchema, breadcrumbSchema } from "../lib/useSeo";
import { CTASection } from "../components/sections/CTASection";
import { SERVICE_CONTENT } from "../lib/serviceContent";

const SERVICES_IMG = "https://images.pexels.com/photos/30547577/pexels-photo-30547577.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const capabilitiesByKey = {
  ai: ["GPT-5.2 / Claude agents", "Workflow automation (n8n / Zapier / custom)", "Internal AI copilots", "RAG & vector search", "Process discovery"],
  web: ["React / Next.js / Astro", "SaaS dashboards & MVPs", "Mobile apps (React Native)", "API design (REST/GraphQL)", "Performance & SEO"],
  marketing: ["Technical SEO audits", "Content engines", "Performance ads (Google / Meta / LinkedIn)", "Email & lifecycle", "Analytics & attribution"],
  content: ["Brand identity systems", "Editorial & social content", "Video & motion", "Web copy & messaging", "Design systems"],
  consulting: ["Architecture reviews", "MVP & roadmap strategy", "Hiring & team setup", "Vendor selection", "Ongoing growth retainer"],
};

const SLUG_BY_KEY = {
  ai: "ai-automation",
  web: "web-development",
  marketing: "digital-marketing",
  content: "content-branding",
  consulting: "strategy-consulting",
};

export default function ServicesPage() {
  const { t, locale } = useI18n();
  useSeo({
    title: locale === "de"
      ? "Leistungen — KI Automatisierung, Webentwicklung, Marketing | Joshi FusionTech Media"
      : "Services — AI Automation, Web Development, Marketing | Joshi FusionTech Media",
    description: locale === "de"
      ? "Unsere Leistungen: KI-Automatisierung, Webentwicklung, digitales Marketing, Branding und Tech-Beratung. Maßgeschneiderte Lösungen für Unternehmen aus Berlin."
      : "Our services: AI automation, web development, digital marketing, branding and tech consulting. Tailored solutions for businesses, from Berlin.",
    canonical: "https://www.joshifusiontech.de/services",
    locale,
    jsonLd: [
      organizationSchema(),
      breadcrumbSchema([
        { name: locale === "de" ? "Start" : "Home", url: "https://www.joshifusiontech.de/" },
        { name: locale === "de" ? "Leistungen" : "Services", url: "https://www.joshifusiontech.de/services" },
      ]),
    ],
  });
  const items = [
    { key: "ai", icon: Cpu },
    { key: "web", icon: Code2 },
    { key: "marketing", icon: BarChart3 },
    { key: "content", icon: Palette },
    { key: "consulting", icon: Compass },
  ];

  return (
    <main data-testid="services-page" className="pt-32">
      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-orange" />
            <span className="label-mono">{t.services.kicker}</span>
          </div>
          <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl tracking-tight text-white">
            {t.services.title}
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">{t.services.subtitle}</p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 lg:row-span-2 glass rounded-3xl overflow-hidden relative min-h-[300px]">
            <img src={SERVICES_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/60 to-transparent" />
            <div className="relative z-10 p-8 md:p-10 h-full flex flex-col justify-end min-h-[300px]">
              <span className="label-mono text-brand-orange mb-3">[ stack ]</span>
              <h3 className="font-display font-bold text-3xl md:text-4xl text-white tracking-tight">One studio. Full delivery.</h3>
              <p className="mt-3 text-white/60 max-w-md">From research and design to deployment and growth — no handoffs, no gaps.</p>
              <div className="mt-6 font-mono text-[11px] tracking-[0.3em] uppercase text-brand-orange">
                Automate · Innovate · Elevate
              </div>
            </div>
          </div>

          {items.map(({ key, icon: Icon }) => {
            const data = t.services[key];
            const slug = SLUG_BY_KEY[key];
            return (
              <Link
                key={key}
                to={`/services/${slug}`}
                data-testid={`service-detail-${key}`}
                className="lg:col-span-7 glass rounded-3xl p-8 md:p-10 hover:border-brand-orange/40 transition-all group block"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl glass-strong flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight flex items-center gap-3">
                      {data.title}
                      <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" />
                    </h3>
                    <p className="mt-2 text-white/60 leading-relaxed max-w-2xl">{data.desc}</p>
                    <div className="mt-6">
                      <div className="label-mono mb-3">{t.services.capabilities}</div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-white/75">
                        {capabilitiesByKey[key].map((c) => (
                          <li key={c} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <CTASection />
    </main>
  );
}
